import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaUsers, FaCheckCircle, FaTimes, FaEye, FaSearch,
  FaClock, FaBuilding, FaEnvelope, FaPhone, FaIdCard,
  FaFileAlt, FaTruck, FaTrailer, FaUserTie, FaShieldAlt,
  FaFilter, FaArrowLeft, FaSpinner
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';

function AdminSubbieVerification({ onLogout }) {
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [filteredApps, setFilteredApps] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [user, setUser] = useState({});
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/subcontractors/pending`);
      const data = await response.json();
      if (data.success) {
        setApplications(data.applications || []);
        setFilteredApps(data.applications || []);
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = applications;
    
    if (searchTerm) {
      filtered = filtered.filter(app => 
        app.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.contact_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.registration_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(app => app.verification_status === filterStatus);
    }
    
    setFilteredApps(filtered);
  }, [searchTerm, filterStatus, applications]);

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-yellow-100 text-yellow-600',
      'under_review': 'bg-blue-100 text-blue-600',
      'pending_docs': 'bg-orange-100 text-orange-600',
      'approved': 'bg-green-100 text-green-600',
      'rejected': 'bg-red-100 text-red-600',
      'suspended': 'bg-gray-100 text-gray-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pending Review',
      'under_review': 'Under Review',
      'pending_docs': 'Pending Docs',
      'approved': 'Approved',
      'rejected': 'Rejected',
      'suspended': 'Suspended'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const pendingCount = applications.filter(a => a.verification_status === 'pending').length;

  if (loading) {
    return (
      <AdminLayout activeTab="subbie-verification" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4 animate-spin">🔄</div>
            <p className="text-gray-500">Loading applications...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="subbie-verification" onLogout={onLogout} user={user}>
      <div className="flex items-center gap-4 mb-6">
        <Link to="/subcontractors" className="p-2 rounded-lg hover:bg-gray-100 transition-colors" style={{ color: '#0A1929' }}>
          <FaArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>
            <FaUsers className="inline mr-2" style={{ color: '#00A86B' }} />
            Subcontractor Verification
          </h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            {pendingCount} pending applications • {applications.length} total
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border mb-6" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <FaFilter className="text-gray-400" />
            <span className="text-sm text-gray-600">Status:</span>
          </div>
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-3 py-1 rounded-full text-sm ${filterStatus === 'all' ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            All ({applications.length})
          </button>
          <button
            onClick={() => setFilterStatus('pending')}
            className={`px-3 py-1 rounded-full text-sm ${filterStatus === 'pending' ? 'bg-yellow-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Pending ({pendingCount})
          </button>
          <button
            onClick={() => setFilterStatus('under_review')}
            className={`px-3 py-1 rounded-full text-sm ${filterStatus === 'under_review' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Under Review
          </button>
          <button
            onClick={() => setFilterStatus('approved')}
            className={`px-3 py-1 rounded-full text-sm ${filterStatus === 'approved' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Approved
          </button>
          <button
            onClick={() => setFilterStatus('rejected')}
            className={`px-3 py-1 rounded-full text-sm ${filterStatus === 'rejected' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Rejected
          </button>
          <div className="flex-1"></div>
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
            <input
              type="text"
              placeholder="Search applications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:border-green w-64"
              style={{ borderColor: '#E8ECEF' }}
            />
          </div>
        </div>
      </div>

      {filteredApps.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-6xl mb-4">🎉</div>
          <h3 className="text-xl font-semibold" style={{ color: '#0A1929' }}>No Applications Found</h3>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            {searchTerm ? 'No applications match your search.' : 'All applications have been reviewed.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredApps.map((app) => (
            <div key={app.id} className="bg-white rounded-xl border p-6 shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
              <div className="flex flex-wrap justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold" style={{ color: '#0A1929' }}>{app.company_name}</h3>
                    <span className={getStatusBadge(app.verification_status)}>
                      {getStatusLabel(app.verification_status)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm" style={{ color: '#4A5568' }}>
                    <span className="flex items-center gap-1">
                      <FaBuilding size={14} /> Reg: {app.registration_number}
                    </span>
                    <span className="flex items-center gap-1">
                      <FaUserTie size={14} /> {app.contact_person}
                    </span>
                    <span className="flex items-center gap-1">
                      <FaEnvelope size={14} /> {app.contact_email}
                    </span>
                    <span className="flex items-center gap-1">
                      <FaPhone size={14} /> {app.contact_phone}
                    </span>
                    <span className="flex items-center gap-1">
                      <FaTruck size={14} /> {app.total_tractors || 0} trucks
                    </span>
                    <span className="flex items-center gap-1">
                      <FaTrailer size={14} /> {app.total_trailers || 0} trailers
                    </span>
                  </div>
                  <p className="text-xs mt-2" style={{ color: '#94A3B8' }}>
                    Submitted: {formatDate(app.application_submitted_at)}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {/* ✅ ONLY "Review" button - No quick approve/reject */}
                  <Link
                    to={`/admin/subbie-verification/${app.id}`}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    style={{ backgroundColor: '#00A86B', color: 'white' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00995E'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
                  >
                    <FaEye size={14} /> Review
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminSubbieVerification;